import React, { useCallback } from 'react'
import { Key } from 'react-aria'
import { useListState } from 'react-stately'
import { dynamicItemToItem } from '../base-select/dynamicItemUtils'
import { SelectItemDataProvider } from '../base-select/SelectItemContext'
import { SelectListBase } from '../select-list/SelectListBase'
import { useSelectListData } from '../select-list/useSelectListData'
import { useSelectListKeyboardDelegate } from '../select-list/useSelectListKeyboardDelegate'
import {
  SelectEndContent,
  SelectItem,
  SelectSection,
  SelectStartContent,
} from '../select/PublicComponents'

type Props = {
  children: React.ReactNode
  onChange: (value: Array<string>) => void
  onClose: () => void
  values: ReadonlyArray<string>
} & (
  | {
      'aria-label': string
      'aria-labelledby'?: never
    }
  | {
      'aria-label'?: never
      'aria-labelledby': string
    }
) &
  (
    | {
        onSearchChange?: (value: string) => void
        search?: string
      }
    | { onSearchChange?: never; search?: never }
  )
export const PillSelectList = ({
  children,
  values,
  onChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  onSearchChange,
  onClose,
  search = '',
}: Props) => {
  const listData = useSelectListData({
    children,
    search,
  })
  const state = useListState({
    disabledKeys: listData.disabledKeys,
    children: dynamicItemToItem,
    items: listData.options,
    selectedKeys: values,
    selectionMode: 'multiple',
    onSelectionChange: selection => {
      if (selection === 'all') {
        return onChange?.([...listData.existingValues.values()])
      }
      return onChange?.([...selection] as Array<string>)
    },
  })
  const onFocusSelectListItem = useCallback(
    (key: Key) => {
      state.selectionManager.setFocused(true)
      state.selectionManager.setFocusedKey(key)
    },
    [state]
  )
  const keyboardDelegate = useSelectListKeyboardDelegate(state.collection, state.disabledKeys)
  return (
    <SelectItemDataProvider value={listData.itemData}>
      {onSearchChange !== undefined ? (
        <SelectListBase
          keyboardDelegate={keyboardDelegate}
          state={state}
          onClose={onClose}
          onSearchChange={onSearchChange}
          onFocusItem={onFocusSelectListItem}
          search={search}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        />
      ) : (
        <SelectListBase
          keyboardDelegate={keyboardDelegate}
          state={state}
          onClose={onClose}
          onFocusItem={onFocusSelectListItem}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        />
      )}
    </SelectItemDataProvider>
  )
}

PillSelectList.Item = SelectItem
PillSelectList.StartContent = SelectStartContent
PillSelectList.EndContent = SelectEndContent
PillSelectList.Section = SelectSection
